import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, Inject, inject, Optional, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardGroupComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormFeedbackComponent,
  FormSelectDirective,
  InputGroupComponent,
  InputGroupTextDirective,
  RowComponent
} from '@coreui/angular-pro';
import { IconDirective, IconModule } from '@coreui/icons-angular';
import { AuthLanguageSwitcherComponent, AUTH_LANGUAGE_SWITCHER_LANGS, I18nService, TranslatePipe } from 'i18n';
import { catchError, debounceTime, map, Observable, of, switchMap } from 'rxjs';
import { Logger, LogoComponent, UnassistedInputDirective } from 'shared';

@Component({
  selector: 'app-register-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    CardComponent,
    CardBodyComponent,
    CardGroupComponent,
    ColComponent,
    ContainerComponent,
    RowComponent,
    FormControlDirective,
    FormSelectDirective,
    FormFeedbackComponent,
    InputGroupComponent,
    InputGroupTextDirective,
    ButtonDirective,
    IconDirective,
    IconModule,
    UnassistedInputDirective,
    TranslatePipe,
    AuthLanguageSwitcherComponent,
    LogoComponent
  ],
  templateUrl: './register-page.component.html',
  styleUrls: ['./register-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterPageComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private http = inject(HttpClient);
  private i18nService = inject(I18nService);
  private destroyRef = inject(DestroyRef);
  protected logger = new Logger('RegisterPageComponent');

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  registerForm: FormGroup;

  captchaToken = signal<string | null>(null);
  captchaError = signal<boolean>(false);
  mcaptchaConfig = signal<{ widgetUrl: string; sitekey: string } | null>(null);
  domainId = signal<number>(0);

  loading = signal<boolean>(false);
  success = signal<boolean>(false);
  error = signal<string | null>(null);

  languages = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' }
  ];

  // Computed — initialized in constructor after registerForm is created
  formStatus!: ReturnType<typeof toSignal<string>>;
  canSubmit!: ReturnType<typeof computed<boolean>>;

  firstNameCtrl = computed(() => this.registerForm.get('first_name')!);
  lastNameCtrl  = computed(() => this.registerForm.get('last_name')!);
  emailCtrl     = computed(() => this.registerForm.get('email')!);
  passwordCtrl  = computed(() => this.registerForm.get('password')!);
  languageCtrl  = computed(() => this.registerForm.get('language')!);

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor(
    @Optional() @Inject(AUTH_LANGUAGE_SWITCHER_LANGS) authSwitcherLangs: string[] | null
  ) {

    this.registerForm = this.fb.group({
      first_name: ['', [Validators.required, Validators.minLength(2)]],
      last_name: [''],
      email: ['', [Validators.required, Validators.email], [this.emailExistsValidator.bind(this)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      sponsor_id: [''],
      language: [this.getDefaultLanguage(), Validators.required]
    });

    this.formStatus = toSignal(this.registerForm.statusChanges, { initialValue: this.registerForm.status });
    this.canSubmit = computed(() =>
      this.formStatus() === 'VALID' &&
      !!this.captchaToken() &&
      !this.loading()
    );

    // Load mCaptcha config + domain info once
    effect(() => {
      this.loadMcaptchaConfig();
      this.loadDomainInfo();
    });

    // Auto-load mCaptcha widget when config is ready
    effect(() => {
      if (this.mcaptchaConfig()) {
        this.loadMcaptchaWidget();
      }
    });

    // Listen for mCaptcha postMessage
    window.addEventListener('message', this.handleMcaptchaMessage.bind(this));

    // Bind the form's language select to the top-right auth language switcher
    // bidirectionally when that switcher is enabled (opt-in via AUTH_LANGUAGE_SWITCHER_LANGS).
    // Apps without the token (e.g. fac3less) keep the standalone form-only behavior.
    if (authSwitcherLangs && authSwitcherLangs.length > 0) {
      this.i18nService.currentLanguage$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((lang: string) => {
          const ctrl = this.languageCtrl();
          if (lang && ctrl.value !== lang && this.languages.some(l => l.code === lang)) {
            ctrl.setValue(lang, { emitEvent: false });
          }
        });

      this.languageCtrl().valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((lang: string) => {
          if (lang && lang !== this.i18nService.getLanguage()) {
            this.i18nService.setLanguage(lang, false);
          }
        });
    }
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  private getDefaultLanguage(): string {
    const current = this.i18nService.getLanguage();
    const browser = navigator.language.split('-')[0] || 'en';
    return this.languages.some(l => l.code === current) ? current :
           this.languages.some(l => l.code === browser) ? browser : 'en';
  }

  private loadMcaptchaConfig(): void {
    this.http.get<{ widgetUrl: string; sitekey: string }>('/auth/mcaptcha_config')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: config => {
            this.logger.debug('mCaptcha config loaded', config);
            this.mcaptchaConfig.set(config);
          },
          error: err => {
            this.logger.error('Failed to load mCaptcha config', err);
            this.error.set('Failed to load captcha configuration. Please refresh.');
          }
        });
  }

  private loadDomainInfo(): void {
    this.http.get<{ domain_id: number }>('/auth/domain_info')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: info => {
            this.logger.debug('Domain info loaded', info);
            this.domainId.set(info.domain_id);
          },
          error: err => this.logger.error('Failed to load domain info', err)
        });
  }

  private loadMcaptchaWidget(): void {
    const config = this.mcaptchaConfig();
    if (!config) return;

    const container = document.getElementById('mcaptcha-widget');
    if (!container) {
      this.logger.error('mCaptcha container not found');
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.src = `${config.widgetUrl}?sitekey=${config.sitekey}`;
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.height = '95px';
    iframe.style.overflow = 'hidden';
    iframe.title = 'mCaptcha verification';
    iframe.setAttribute('scrolling', 'no');

    container.innerHTML = ''; // Clear previous content
    container.appendChild(iframe);
  }

  private handleMcaptchaMessage(event: MessageEvent): void {
    const widgetUrl = this.mcaptchaConfig()?.widgetUrl;
    if (!widgetUrl) return;
    let expectedOrigin: string;
    try {
      expectedOrigin = new URL(widgetUrl).origin;
    } catch {
      return;
    }
    if (event.origin !== expectedOrigin) return;

    const data = event.data;
    let token: string | null = null;

    if (typeof data === 'string') {
      token = data;
    } else if (data?.token) {
      token = data.token;
    } else if (data?.m_captcha_token) {
      token = data.m_captcha_token;
    }

    if (token) {
      this.captchaToken.set(token);
      this.captchaError.set(false);
      this.logger.debug('mCaptcha token received');
    }
  }

  emailExistsValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    if (!control.value) return of(null);

    return of(control.value).pipe(
      debounceTime(500),
      switchMap(email =>
        this.http.get<{ exists: boolean }>(`/auth/check_email?email=${encodeURIComponent(email)}`).pipe(
          map(res => res.exists ? { emailExists: true } : null),
          catchError(() => of(null))
        )
      )
    );
  }

  onSubmit(): void {
    if (this.registerForm.invalid || !this.captchaToken()) {
      if (!this.captchaToken()) this.captchaError.set(true);
      this.registerForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const data = {
      ...this.registerForm.value,
      captcha_token: this.captchaToken()
    };

    this.http.post('/auth/newuser', data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.success.set(true);
          this.error.set(null);
          this.loading.set(false);
          setTimeout(() => this.router.navigate(['/login']), 2000);
        },
        error: err => {
          this.error.set(err.error?.error || 'Registration failed. Please try again.');
          this.loading.set(false);
          this.captchaToken.set(null); // Reset captcha on error
        }
      });
  }
}